import Phaser from 'phaser';
import BattleStateManager from '../game/BattleStateManager.js';
import VoteManager from '../services/VoteManager.js';
import { ensureThemeLoaded, getThemeData, buildItemsById } from '../Utils';

/**
 * WrapUpScene - Battle summary and rewards scene
 * 
 * This scene displays the battle summary for 20 seconds, showing top players,
 * XP gained, loot earned, and battle statistics. It creates FOMO for rewards
 * and then returns to the JoinScene.
 * 
 * @class WrapUpScene
 */
export class WrapUpScene extends Phaser.Scene {
  constructor() {
    super({ key: 'WrapUpScene' });
    this.displayDuration = 7000; // 20 seconds
    this.startTime = null;
    this.topPlayers = [];
    this.battleRewards = null;
    this.rewardsScript = null;
    this.predictionResultsPromise = null;
    this.predictionResultsReady = false;
    this.predictionResults = null;
    this.hasPredictionResults = false;
    this._transitioning = false;
    this.itemsById = null;
  }

  init(data) {
    // Ensure item metadata is available for loot display
    const cachedItems = getThemeData('items', null);
    if (Array.isArray(cachedItems) && cachedItems.length) {
      this.itemsById = buildItemsById(cachedItems);
    } else {
      ensureThemeLoaded()
        .then(() => {
          const themedItems = getThemeData('items', []);
          this.itemsById = buildItemsById(themedItems);
        })
        .catch((err) => {
          console.warn('[WrapUpScene] Failed to ensure theme items loaded:', err);
        });
    }

    // Get battle results from scene data or BattleStateManager
    this.battleResults = data?.battleResults || BattleStateManager.getBattleResults() || {};
    this.winners = data?.winners || BattleStateManager.getWinners() || [];
    this.allPlayers = data?.allPlayers || BattleStateManager.getPlayerList() || [];
    this.battleRewards = data?.battleRewards || BattleStateManager.getBattleRewards() || null;
    this.predictionResultsPromise = null;
    this.predictionResultsReady = false;
    this.predictionResults = null;
    this.hasPredictionResults = false;
    this._transitioning = false;
    
    // Generate real top players from battle results if available
    this.generateTopPlayersFromResults();

    this.rewardsScript = this.generateRewardsScript();
    BattleStateManager.setRewardsScript(this.rewardsScript);
    
    console.log('📊 WrapUpScene initialized:', {
      playerCount: this.allPlayers.length,
      winnerCount: this.winners.length,
      topPlayersCount: this.topPlayers.length,
      hasRealData: !!(this.battleResults?.playerRankings)
    });
    if (this.battleRewards) {
      console.log('🎁 WrapUpScene received battle rewards data:', this.battleRewards);
    }
  }

  preload() {
    // Load UI assets
    this.load.image('trophy', '/assets/ui/trophy.png');
    this.load.image('medal', '/assets/ui/medal.png');
    this.load.image('coin', '/assets/ui/coin.png');
    this.load.image('xp-icon', '/assets/ui/xp.png');
    this.load.image('loot-chest', '/assets/ui/loot-chest.png');
  }

  create() {
    const { width, height } = this.cameras.main;
    
    // Create dark background
    this.background = this.add.rectangle(width / 2, height / 2, width, height, 0x0F172A);
    
    // Add subtle gradient overlay
    this.gradientOverlay = this.add.rectangle(width / 2, height / 2, width, height, 0x1E293B);
    this.gradientOverlay.setAlpha(0.2);
    
    // Create main content container
    this.contentContainer = this.add.container(width / 2, height / 2).setDepth(10);
    
    // Create battle summary UI
    this.createBattleSummaryUI(width, height);
    
    // Start the display timer (delayed by 1 second for fade-in)
    this.startTime = this.time.now + 1000; // Add 1 second delay for fade-in
    
    // Determine whether there are prediction results to show
    this.predictionResultsPromise = this.checkPredictionResults();

    // Fade in animation
    this.contentContainer.setAlpha(0);
    this.tweens.add({
      targets: this.contentContainer,
      alpha: 1,
      duration: 800,
      ease: 'Power2'
    });
  }

  createBattleSummaryUI(width, height) {
    // Main summary container
    const summaryWidth = Math.min(1000, width * 0.95);
    const summaryHeight = Math.min(800, height * 0.9);
    
    // Background panel
    const panelBg = this.add.rectangle(0, 0, summaryWidth, summaryHeight, 0x1E293B);
    panelBg.setStrokeStyle(3, 0x3B82F6);
    panelBg.setAlpha(0.95);
    this.contentContainer.add(panelBg);
    
    // Title
    const title = this.add.text(0, -summaryHeight / 2 + 40, 'Battle Summary', {
      fontSize: '36px',
      color: '#F3F300',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5);
    this.contentContainer.add(title);
    
    // Create sections
    this.createTopPlayersSection(summaryWidth, summaryHeight);
    //this.createRewardsSection(summaryWidth, summaryHeight);
    //this.createBattleStatsSection(summaryWidth, summaryHeight);
    //this.createFOMOSection(summaryWidth, summaryHeight);
    
    // Create countdown progress bar at the bottom
    this.createProgressBar(summaryWidth, summaryHeight);
  }
  
  createProgressBar(containerWidth, containerHeight) {
    const barWidth = containerWidth * 0.8;
    const barHeight = 20;
    const barY = containerHeight / 2 - 40;
    
    // Background bar (full width, dark)
    this.progressBarBg = this.add.rectangle(0, barY, barWidth, barHeight, 0x1E293B);
    this.progressBarBg.setStrokeStyle(2, 0xFFFFFF);
    this.progressBarBg.setAlpha(0); // Start invisible
    this.contentContainer.add(this.progressBarBg);
    
    // Progress bar fill (starts full width, shrinks as time passes)
    this.progressBarFill = this.add.rectangle(-barWidth / 2, barY, barWidth, barHeight - 4, 0xF3F300);
    this.progressBarFill.setOrigin(0, 0.5);
    this.progressBarFill.setAlpha(0); // Start invisible
    this.contentContainer.add(this.progressBarFill);
    
    // Add glow effect to progress bar
    const progressGlow = this.add.rectangle(0, barY, barWidth, barHeight + 4, 0xF3F300);
    progressGlow.setAlpha(0); // Start invisible
    progressGlow.setBlendMode(Phaser.BlendModes.ADD);
    this.contentContainer.addAt(progressGlow, this.contentContainer.list.indexOf(this.progressBarBg));
    
    // Fade in progress bar after 1 second
    this.time.delayedCall(1000, () => {
      this.tweens.add({
        targets: [this.progressBarBg, this.progressBarFill, progressGlow],
        alpha: { from: 0, to: 1 },
        duration: 500,
        ease: 'Power2',
        onComplete: () => {
          // Set final alpha values
          this.progressBarBg.setAlpha(0.8);
          this.progressBarFill.setAlpha(0.9);
          progressGlow.setAlpha(0.2);
        }
      });
    });
  }

  createTopPlayersSection(summaryWidth, summaryHeight) {
    const sectionY = -summaryHeight / 2 + 100;
    
    // Section title
    const sectionTitle = this.add.text(0, sectionY, '🏆 Top Players', {
      fontSize: '24px',
      color: '#F3F300',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.contentContainer.add(sectionTitle);
    
    // Top 5 players leaderboard
    const leaderboardY = sectionY + 50;
    const maxPlayers = Math.min(10, this.topPlayers.length);
    
    for (let i = 0; i < maxPlayers; i++) {
      const player = this.topPlayers[i];
      const playerY = leaderboardY + (i * 50);
      
      // Rank icon
      let rankIcon;
      if (i === 0) {
        rankIcon = this.add.image(-summaryWidth / 2 + 150, playerY, 'trophy');
        rankIcon.setScale(0.1);
        rankIcon.setTint(0xFFD700); // Gold
      } else if (i === 1) {
        rankIcon = this.add.image(-summaryWidth / 2 + 150, playerY, 'medal');
        rankIcon.setScale(0.1);
        rankIcon.setTint(0xC0C0C0); // Silver
      } else if (i === 2) {
        rankIcon = this.add.image(-summaryWidth / 2 + 150, playerY, 'medal');
        rankIcon.setScale(0.1);
        rankIcon.setTint(0xCD7F32); // Bronze
      } else {
        rankIcon = this.add.text(-summaryWidth / 2 + 150, playerY, `${i + 1}.`, {
          fontSize: '28px',
          color: '#E2E8F0',
          fontFamily: 'Arial, sans-serif',
          fontStyle: 'bold'
        }).setOrigin(0.5);
      }
      this.contentContainer.add(rankIcon);
      
      // Player name
      const playerName = player.name || player.hero?.name || 'Unknown Hero';
      console.log('[WrapUpScene] Player name:', playerName);
      console.log('[WrapUpScene] Player:', player);
      const nameText = this.add.text(-summaryWidth / 2 + 200, playerY, playerName, {
        fontSize: '32px',
        color: '#E2E8F0',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold'
      }).setOrigin(0, 0.5);
      this.contentContainer.add(nameText);
      
      // Player stats
      const eliminations = player.eliminations || 0;
      const damage = parseFloat(player.damageDealt || 0).toFixed(0);
      const statsText = this.add.text(summaryWidth / 2 - 180, playerY, `${eliminations} kills | ${damage} dmg`, {
        fontSize: '32px',
        color: '#9CA3AF',
        fontFamily: 'Arial, sans-serif'
      }).setOrigin(1, 0.5);
      this.contentContainer.add(statsText);
    }
  }

  createRewardsSection(summaryWidth, summaryHeight) {
    const sectionY = -summaryHeight / 2 + 400;
    
    // Section title
    const sectionTitle = this.add.text(0, sectionY, '💰 Rewards Earned', {
      fontSize: '24px',
      color: '#10B981',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.contentContainer.add(sectionTitle);
    
    // Rewards grid
    const rewardsY = sectionY + 50;
    const rewards = [
      { icon: 'xp', label: 'XP Gained', value: this.battleResults.totalXP || 0, color: '#8B5CF6' },
      { icon: 'coin', label: 'Credits', value: this.battleResults.totalCredits || 0, color: '#F59E0B' },
      { icon: 'loot-chest', label: 'Loot Items', value: this.battleResults.lootItems || 0, color: '#EF4444' }
    ];
    
    rewards.forEach((reward, index) => {
      const rewardX = -summaryWidth / 2 + 150 + (index * 200);
      
      // Reward icon
      const icon = this.add.image(rewardX, rewardsY, reward.icon);
      icon.setScale(0.1);
      this.contentContainer.add(icon);
      
      // Reward value
      const valueText = this.add.text(rewardX, rewardsY + 30, reward.value.toLocaleString(), {
        fontSize: '20px',
        color: reward.color,
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold'
      }).setOrigin(0.5);
      this.contentContainer.add(valueText);
      
      // Reward label
      const labelText = this.add.text(rewardX, rewardsY + 50, reward.label, {
        fontSize: '14px',
        color: '#9CA3AF',
        fontFamily: 'Arial, sans-serif'
      }).setOrigin(0.5);
      this.contentContainer.add(labelText);
    });
  }

  createBattleStatsSection(summaryWidth, summaryHeight) {
    const sectionY = -summaryHeight / 2 + 550;
    
    // Section title
    const sectionTitle = this.add.text(0, sectionY, '📈 Battle Statistics', {
      fontSize: '24px',
      color: '#3B82F6',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.contentContainer.add(sectionTitle);
    
    // Stats grid
    const statsY = sectionY + 40;
    const stats = [
      { label: 'Duration', value: this.battleResults.duration || '2:30' },
      { label: 'Total Eliminations', value: this.battleResults.totalEliminations || 0 },
      { label: 'Game Mode', value: this.battleResults.gameMode || 'Free-for-All' },
      { label: 'Battlefield', value: this.battleResults.battlefield || 'Ancient Ruins' }
    ];
    
    stats.forEach((stat, index) => {
      const statX = -summaryWidth / 2 + 200 + (index * 200);
      
      // Stat label
      const labelText = this.add.text(statX, statsY, stat.label, {
        fontSize: '14px',
        color: '#9CA3AF',
        fontFamily: 'Arial, sans-serif'
      }).setOrigin(0.5);
      this.contentContainer.add(labelText);
      
      // Stat value
      const valueText = this.add.text(statX, statsY + 20, stat.value.toString(), {
        fontSize: '16px',
        color: '#E2E8F0',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold'
      }).setOrigin(0.5);
      this.contentContainer.add(valueText);
    });
  }

  createFOMOSection(summaryWidth, summaryHeight) {
    const sectionY = summaryHeight / 2 - 100;
    
    // FOMO message
    const fomoText = this.add.text(0, sectionY, '🎯 Ready for the next battle?', {
      fontSize: '28px',
      color: '#F3F300',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    this.contentContainer.add(fomoText);
    
    // Animate FOMO text
    this.tweens.add({
      targets: fomoText,
      scaleX: { from: 1, to: 1.05 },
      scaleY: { from: 1, to: 1.05 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // Call to action
    const ctaText = this.add.text(0, sectionY + 40, 'Join the next battle to earn more rewards!', {
      fontSize: '18px',
      color: '#10B981',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.contentContainer.add(ctaText);
  }

  generateTopPlayersFromResults() {
    // Use real battle results if available, otherwise generate mock data
    if (this.battleResults?.playerRankings && this.battleResults.playerRankings.length > 0) {
      console.log('📊 Using real battle results for top players');
      
      // Extract real player data from battle results
      this.topPlayers = this.battleResults.playerRankings
        .map(ranking => ({
          ...ranking.player,
          eliminations: ranking.eliminations || 0,
          damageDealt: ranking.player?.damageDealt || 0,
          damageTaken: ranking.player?.damageTaken || 0,
          finalPosition: ranking.finalPosition || 0,
          isWinner: ranking.isWinner || false,
          survived: ranking.survived || false
        }))
        .sort((a, b) => b.eliminations - a.eliminations)
        .slice(0, 10);
        
      // Fill in missing battle result data with defaults
      if (!this.battleResults.duration) {
        this.battleResults.duration = this.battleResults.battleDuration ? 
          `${Math.floor(this.battleResults.battleDuration / 60)}:${(this.battleResults.battleDuration % 60).toString().padStart(2, '0')}` : 
          '2:30';
      }
      if (!this.battleResults.totalEliminations) {
        this.battleResults.totalEliminations = this.topPlayers.reduce((total, player) => total + (player.eliminations || 0), 0);
      }
      if (!this.battleResults.gameMode) {
        this.battleResults.gameMode = 'Free-for-All';
      }
      if (!this.battleResults.battlefield) {
        this.battleResults.battlefield = 'Ancient Ruins';
      }
    } else {
      console.log('📊 No real battle results available, generating mock data');
      this.generateMockBattleResults();
    }
  }

  generateMockBattleResults() {
    // Generate mock data if not provided
    if (!this.battleResults.duration) {
      this.battleResults.duration = '2:30';
    }
    if (!this.battleResults.totalEliminations) {
      this.battleResults.totalEliminations = Math.floor(Math.random() * 20) + 5;
    }
    if (!this.battleResults.gameMode) {
      this.battleResults.gameMode = 'Free-for-All';
    }
    if (!this.battleResults.battlefield) {
      this.battleResults.battlefield = 'Ancient Ruins';
    }
    if (!this.battleResults.totalXP) {
      this.battleResults.totalXP = Math.floor(Math.random() * 1000) + 500;
    }
    if (!this.battleResults.totalCredits) {
      this.battleResults.totalCredits = Math.floor(Math.random() * 500) + 200;
    }
    if (!this.battleResults.lootItems) {
      this.battleResults.lootItems = Math.floor(Math.random() * 5) + 1;
    }
    
    // Generate top players from all players
    this.topPlayers = this.allPlayers.map(player => ({
      ...player,
      eliminations: Math.floor(Math.random() * 10),
      damageDealt: Math.floor(Math.random() * 2000) + 500,
      xpEarned: Math.floor(Math.random() * 200) + 50,
      creditsEarned: Math.floor(Math.random() * 100) + 25
    })).sort((a, b) => b.eliminations - a.eliminations).slice(0, 10);
  }

  update(time, delta) {
    if (!this.startTime) return;
    
    const elapsed = time - this.startTime;
    const remaining = Math.max(0, this.displayDuration - elapsed);
    const progress = Math.max(0, Math.min(1, remaining / this.displayDuration));
    const remainingSeconds = Math.ceil(remaining / 1000);
    
    // Update progress bar
    if (this.progressBarFill && this.progressBarBg) {
      const barWidth = this.progressBarBg.width;
      const newWidth = barWidth * progress;
      this.progressBarFill.width = newWidth;
      this.progressBarFill.x = -barWidth / 2;
      
      // Change color based on remaining time
      if (remainingSeconds <= 3) {
        this.progressBarFill.setFillStyle(0xEF4444); // Red
      } else if (remainingSeconds <= 6) {
        this.progressBarFill.setFillStyle(0xF59E0B); // Orange
      } else {
        this.progressBarFill.setFillStyle(0xF3F300); // Yellow
      }
    }
    
    // Transition to PredictionResultsScene when time is up
    if (remaining <= 0) {
      if (!this.predictionResultsReady) {
        if (!this.predictionResultsPromise) {
          this.predictionResultsPromise = this.checkPredictionResults();
        }
        return;
      }
      if (this._transitioning) return;
      this._transitioning = true;

      if (this.hasPredictionResults) {
        console.log('➡️ Transitioning to PredictionResultsScene...');
        if (!this._resultsPosted) {
          this._resultsPosted = true;
          try { this.calculateAndPostVoteResults(); } catch (_) {}
        }
        this.scene.start('PredictionResultsScene', {
          rewardsScript: this.rewardsScript,
          predictionResults: this.predictionResults,
          autoTransitionDelay: 10000
        });
      } else {
        console.log('➡️ Transitioning to RewardsScene (no predictions)');
        this.scene.start('RewardsScene', {
          rewardsScript: this.rewardsScript
        });
      }
    }
  }

  async checkPredictionResults() {
    if (this.predictionResultsReady) return;
    try {
      const sessionId = VoteManager.getSessionId ? VoteManager.getSessionId() : BattleStateManager.getVoteSessionId?.();
      if (!sessionId) {
        this.hasPredictionResults = false;
        return;
      }
      const results = await VoteManager.getResults();
      if (results && Array.isArray(results.results) && results.results.length > 0) {
        this.hasPredictionResults = true;
        this.predictionResults = results;
      } else {
        this.hasPredictionResults = false;
      }
    } catch (error) {
      console.warn('[WrapUpScene] Failed to check prediction results:', error);
      this.hasPredictionResults = false;
    } finally {
      this.predictionResultsReady = true;
    }
  }

  async calculateAndPostVoteResults() {
    try {
      const sessionId = BattleStateManager.getVoteSessionId();
      if (!sessionId) return;

      const rankings = this.battleResults?.playerRankings || [];
      if (!rankings.length) return;

      const finalPositions = rankings
        .filter(r => r?.player?.heroId)
        .map(r => ({ heroId: r.player.heroId, position: r.finalPosition }));

      if (!finalPositions.length) return;

      const apiKey = process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key';
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('jwt_token') : null;
      await fetch(`/api/votes/${sessionId}/results`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ finalPositions })
      });
    } catch (e) {
      console.error('[WrapUpScene] Failed to post vote results:', e);
    }
  }

  generateRewardsScript() {
    const script = this.buildRewardsScriptFromData();
    if (script) {
      console.log('🎁 Generated rewards script from battle data:', script);
      return script;
    }
    const fallback = this.generateMockRewardsScript();
    console.warn('🎁 Falling back to mock rewards script');
    return fallback;
  }

  buildRewardsScriptFromData() {
    if (!this.battleRewards || !Array.isArray(this.battleRewards.heroResults) || this.battleRewards.heroResults.length === 0) {
      return null;
    }

    const playerList = BattleStateManager.getPlayerList() || [];
    const playerMap = new Map();
    playerList.forEach(player => {
      const heroId = player?.hero?.id;
      if (heroId) {
        playerMap.set(heroId, player);
      }
    });

    if (Array.isArray(this.battleResults?.playerRankings)) {
      this.battleResults.playerRankings.forEach(ranking => {
        const heroId = ranking?.player?.heroId;
        if (heroId && ranking.player) {
          playerMap.set(heroId, ranking.player);
        }
      });
    }

    const players = this.battleRewards.heroResults.map((heroResult, index) => {
      const heroId = heroResult.heroId;
      const basePlayer = heroId ? (playerMap.get(heroId) || BattleStateManager.findPlayerByHeroId?.(heroId)) : null;
      const heroData = basePlayer?.hero || {};

      const name = heroData.name || basePlayer?.name || `Hero ${index + 1}`;
      const level = heroData.level || basePlayer?.level || 1;
      const imageUrl = heroData.image_url || basePlayer?.imageUrl || null;
      const isRealHero = !!(heroData.id || heroId);

      const stats = heroResult.stats || {};
      const matchStats = {
        finalPosition: stats.finalPosition ?? (index + 1),
        kills: stats.eliminations || 0,
        deaths: stats.deaths || 0,
        damageDealt: Math.round(stats.damageDealt || 0),
        damageTaken: Math.round(stats.damageTaken || 0),
        survivalTime: stats.survivalTime || 0
      };

      const { lootItems, specialFinds } = this.transformLootEntries(stats.loot);

      const xpEarned = heroResult.xpEarned || 0;
      return {
        id: basePlayer?.id || heroId || `player_${index}`,
        name,
        level,
        isRealHero,
        heroId,
        imageUrl,
        isRandom: !!basePlayer?.isRandom,
        matchStats,
        rewards: {
          xpEarned,
          leveledUp: false,
          newLevel: level,
          lootItems,
          specialFinds
        }
      };
    });

    const totals = this.battleRewards.totals || {
      playerCount: players.length,
      totalXpEarned: players.reduce((sum, player) => sum + (player.rewards?.xpEarned || 0), 0)
    };

    const battlefield = this.battleResults?.battlefield || BattleStateManager.getBattlefield() || null;

    return {
      battleId: this.battleRewards.battleId || this.battleResults?.battleId || `battle_${Date.now()}`,
      timestamp: new Date().toISOString(),
      battlefield,
      players,
      totals,
      globalTiming: {
        playerTransitionDelay: 600,
        totalDuration: Math.max(45000, players.length * 4500)
      }
    };
  }

  transformLootEntries(lootEntries = []) {
    const lootItems = [];
    const specialFinds = [];

    lootEntries.forEach((entry, index) => {
      if (!entry) return;
      console.log('[WrapUpScene] transformLootEntries entry:', entry);
      const type = entry.lootType || entry.loot_type;
      const quantity = entry.qty || entry.quantity || entry.amount || 1;
      if (type === 'token') {
        const tokenSymbol = entry.tokenSymbol || 'Token';
        lootItems.push({
          itemId: entry.itemId || `token_${index}`,
          itemName: `${quantity} ${tokenSymbol}`,
          rarity: 'token',
          quantity,
          tokenSymbol
        });
        return;
      }

      const itemId = entry.itemId || entry.themeItemId || `item_${index}`;
      const itemDef = this.resolveItemDefinition(itemId);
      const itemName =
        entry.itemName ||
        itemDef?.name ||
        entry.themeItemId ||
        itemId;
      const iconKey =
        entry.iconKey ||
        itemDef?.iconKey ||
        itemDef?.icon_key ||
        null;
      const rarity =
        entry.rarity ||
        itemDef?.rarity ||
        'common';

      lootItems.push({
        itemId,
        itemName,
        rarity,
        quantity,
        iconKey
      });
    });

    return { lootItems, specialFinds };
  }

  resolveItemDefinition(itemId) {
    if (!itemId || !this.itemsById) return null;
    const key = String(itemId);
    if (this.itemsById.has(key)) return this.itemsById.get(key);
    return this.itemsById.get(itemId) || null;
  }

  generateMockRewardsScript() {
    const playerList = BattleStateManager.getPlayerList();
    const battlefield = BattleStateManager.getBattlefield();
    const eligiblePlayers = playerList.filter(player => !player.isRandom);

    console.log(`🎁 Filtering players for rewards (mock): ${playerList.length} total, ${eligiblePlayers.length} eligible`);

    const battleId = `battle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return {
      battleId: battleId,
      timestamp: new Date().toISOString(),
      battlefield: battlefield,
      players: eligiblePlayers.map((player, index) => {
        const mockStats = {
          kills: Math.floor(Math.random() * 5),
          deaths: Math.floor(Math.random() * 3),
          damageDealt: Math.floor(Math.random() * 1000) + 200,
          survivalTime: Math.floor(Math.random() * 200) + 60
        };

        const baseXP = 50;
        const performanceXP = (mockStats.kills * 20) + (mockStats.damageDealt / 10) + (mockStats.survivalTime / 2);
        const xpEarned = Math.floor(baseXP + performanceXP);

        const currentLevel = player.hero?.level || 1;
        const xpNeededForNextLevel = currentLevel * 100;
        const leveledUp = xpEarned >= xpNeededForNextLevel;
        const newLevel = leveledUp ? currentLevel + 1 : currentLevel;

        const lootItems = this.generateLootItems(mockStats);
        const specialFinds = this.generateSpecialFinds(mockStats);

        return {
          id: player.id || `player_${index}`,
          name: player.hero?.name || player.name || 'Unknown Hero',
          level: currentLevel,
          isRealHero: !!(player.hero && player.hero.id),
          heroId: player.hero?.id || null,
          imageUrl: player.hero?.image_url || null,
          matchStats: mockStats,
          rewards: {
            xpEarned: xpEarned,
            leveledUp: leveledUp,
            newLevel: newLevel,
            lootItems: lootItems,
            specialFinds: specialFinds
          }
        };
      }),
      globalTiming: {
        playerTransitionDelay: 500,
        totalDuration: 45000
      }
    };
  }
  
  generateLootItems(stats) {
    const lootItems = [];
    
    // Base loot chance
    const lootChance = 0.7; // 70% chance of getting loot
    
    if (Math.random() < lootChance) {
      const itemTypes = [
        { name: 'Health Potion', rarity: 'common', quantity: Math.floor(Math.random() * 3) + 1 },
        { name: 'Iron Sword', rarity: 'common', quantity: 1 },
        { name: 'Steel Armor', rarity: 'uncommon', quantity: 1 },
        { name: 'Magic Ring', rarity: 'rare', quantity: 1 },
        { name: 'Dragon Scale', rarity: 'epic', quantity: 1 }
      ];
      
      // Higher performance = better loot
      const performanceMultiplier = Math.min(2, (stats.kills + stats.damageDealt / 200) / 3);
      const numItems = Math.floor(Math.random() * Math.ceil(performanceMultiplier)) + 1;
      
      for (let i = 0; i < numItems; i++) {
        const item = itemTypes[Math.floor(Math.random() * itemTypes.length)];
        lootItems.push({
          itemId: `item_${Date.now()}_${i}`,
          itemName: item.name,
          rarity: item.rarity,
          quantity: item.quantity
        });
      }
    }
    
    return lootItems;
  }
  
  generateSpecialFinds(stats) {
    const specialFinds = [];
    
    // Special find chance based on performance
    const specialChance = Math.min(0.3, stats.kills * 0.05 + stats.damageDealt / 2000);
    
    if (Math.random() < specialChance) {
      const specialTypes = [
        { type: 'unique', message: 'Unique Discovered!' },
        { type: 'legendary', message: 'Legendary Item Found!' },
        { type: 'rare', message: 'Rare Artifact Uncovered!' }
      ];
      
      const special = specialTypes[Math.floor(Math.random() * specialTypes.length)];
      specialFinds.push({
        type: special.type,
        message: special.message,
        itemId: `special_${Date.now()}`
      });
    }
    
    return specialFinds;
  }

  shutdown() {
    // Clean up any resources if needed
    console.log('📊 WrapUpScene shutdown');
  }
}
